/**
 * The draw-site half of a texture view: a consumer states the sampler it needs and draws the
 * texture it gets back, which the hook has made once, uploaded in bands where large, and frees once
 * nothing draws it.
 */
import { useMemo } from 'react';
import { ownedTextureView, sameViewSampler, textureView, viewToRelease, } from '../resources/textures/textureView';
import { useByValue } from './hooks/useByValue';
import { useTiledUpload } from './tiledUpload/useTiledUpload';
import { useDisposeOnRelease } from './useDisposeOnRelease';
import { isLiveTarget } from './liveTargets';
import { useUvWindow } from './useUvWindow';
/**
 * `source` as `sampler` samples it: the source itself when it already does, else one clone. Null
 * without a source, and the source itself without a sampler. A sampler is compared by value, so a
 * render that states the same values keeps the view and uploads nothing.
 *
 * @canonical A texture view a component draws
 *   `useOwnedTextureView` for a consumer that writes the clone.
 */
export function useTextureView(source, sampler) {
    return useView(source, sampler, sharedView);
}
/**
 * `useTextureView` that always draws a clone, which the consumer may write, as a sprite moves its
 * window to the next frame. Null without a source or a sampler: a consumer with nothing to sample
 * states none.
 */
export function useOwnedTextureView(source, sampler) {
    return useView(source, sampler, ownedView);
}
/**
 * `useOwnedTextureView` that shows `window`. A clone takes the window by a write, so a moving window
 * keeps the clone. A live target takes it in the sampler, so only a reader whose window or sampler
 * differs from the target's reads its own copy, which may be of the window's cell and shows the
 * window in the cell's own coordinates.
 */
export function useWindowedTextureView(source, sampler, window) {
    const isLive = source ? isLiveTarget(source) : false;
    const view = useOwnedTextureView(source, sampler && isLive ? { ...sampler, window } : sampler);
    useUvWindow(isLive ? null : view, window);
    return view;
}
const sharedView = (source, sampler) => (sampler ? textureView(source, sampler) : source);
const ownedView = (source, sampler) => (sampler ? ownedTextureView(source, sampler) : null);
function useView(source, sampler, make) {
    const stated = useByValue(sampler, sameViewSampler);
    const view = useMemo(() => (source ? make(source, stated) : null), [source, stated, make]);
    return useDrawnView(view);
}
/**
 * Both the view and the texture on screen are held: a texture is freed once it is neither, so the
 * previous view keeps drawing while a large new one uploads.
 */
function useDrawnView(view) {
    const drawn = useTiledUpload(view);
    useDisposeOnRelease(viewToRelease(view));
    // One hold per object: the drawn texture is the view itself except while a new one uploads.
    useDisposeOnRelease(drawn === view ? null : viewToRelease(drawn));
    return drawn;
}
//# sourceMappingURL=useTextureView.js.map